import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from 'react-bootstrap';

import subscriptionsConfig from '../config/subscriptions';
import PricingTable from '../containers/Onboarding/Subscription/PricingTable';
import { useCurrentUserInfo, useProduct } from '../hooks';
import useSubscriptionCheckout from '../hooks/useSubscriptionCheckout';

import ErrorLabel from './ErrorLabel';
import LoopSpinner from './LoopSpinner';

const stripePromise = loadStripe(subscriptionsConfig.pk);

/**
 * Subscription checkout component that displays pricing options and handles the Stripe checkout flow.
 * Used in both onboarding and subscription edit flows.
 * @param returnUrl - URL to redirect to after successful checkout
 */
const SubscriptionCheckout = ({ returnUrl }: { returnUrl: string }) => {
  const { user, loading: userLoading, error: userError } = useCurrentUserInfo();
  const { product, loading: productLoading, error: productError, retry: retryProduct } = useProduct();

  const {
    checkoutSession,
    checkoutRequestPending,
    checkoutRequestError,
    handlePriceSelect,
    clearCheckoutError,
    canRetryCheckout,
    checkoutIsNetworkIssue,
    handleRetry: retryCheckout,
  } = useSubscriptionCheckout(returnUrl);

  const loading = userLoading || productLoading;
  const error = userError || productError || checkoutRequestError;

  /**
   * Handle retry action - clears any checkout errors and re-fetches product data.
   * Also retries the checkout if there was a previous price selection.
   */
  const handleRetry = () => {
    clearCheckoutError();
    if (canRetryCheckout) {
      retryCheckout();
    } else {
      void retryProduct();
    }
  };

  return (
    <>
      {!checkoutSession && <h1 className="mb-3">Choose your subscription type</h1>}

      {loading && <LoopSpinner />}

      {!!error && !loading && (
        <div className="text-center w-100">
          <ErrorLabel label="We're having trouble loading your subscription options" />
          {checkoutIsNetworkIssue && (
            <p className="text-muted mt-2">Network connection lost. Please check your connection and try again.</p>
          )}
          {!checkoutIsNetworkIssue && (
            <p className="text-muted mt-2">Please try again or contact support if the problem persists.</p>
          )}
          <Button
            variant="primary"
            type="button"
            className="mt-3 w-100"
            onClick={handleRetry}
            disabled={checkoutRequestPending}
          >
            Retry
          </Button>
        </div>
      )}

      {!checkoutSession && !!product && !!user && !error && (
        <PricingTable
          product={product}
          onSelect={handlePriceSelect}
          requestPending={checkoutRequestPending}
          disabled={checkoutIsNetworkIssue}
        />
      )}

      {!!checkoutSession && (
        <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret: checkoutSession.client_secret }}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      )}
    </>
  );
};

export default SubscriptionCheckout;
